import { useEffect, useRef, useState, useCallback } from 'react';
import { useWindowSize } from '@react-hook/window-size';
import {
  INITIAL_VALUE,
  ReactSVGPanZoom,
  TOOL_NONE,
  fitSelection,
  zoomOnViewerCenter,
  fitToViewer,
  ViewerTouchEvent,
  TOOL_PAN,
  TOOL_ZOOM_IN,
  TOOL_ZOOM_OUT,
  TOOL_AUTO,
} from 'react-svg-pan-zoom';
import * as cn from 'classnames';
import * as _ from 'lodash';
import { activityNodes } from './activity-nodes';
import { useModal } from '../../../contexts/modal-context';
import './ecosystem.css';
import { useFilter, useFilterDispatch } from '../../../contexts/filter-context';
import { useHover, useHoverDispatch } from '../../../contexts/hover-context';
import { ReactComponent as Select } from '../../../assets/select.svg';
import { ReactComponent as SelectFill } from '../../../assets/select-fill.svg';
import { ReactComponent as Pan } from '../../../assets/pan.svg';
import { ReactComponent as PanFill } from '../../../assets/pan-fill.svg';
import { ReactComponent as ZoomIn } from '../../../assets/zoom-in.svg';
import { ReactComponent as ZoomInFill } from '../../../assets/zoom-in-fill.svg';
import { ReactComponent as ZoomOut } from '../../../assets/zoom-out.svg';
import { ReactComponent as ZoomOutFill } from '../../../assets/zoom-out-fill.svg';
import { ReactComponent as Fit } from '../../../assets/fit.svg';
import { ReactComponent as Question } from '../../../assets/question.svg';
import { Button } from '../../widgets/button/button';

const Mini = () => {
  return <div></div>;
};

export const Ecosystem = ({ ecosystemRoles: roles, ecosystemActivities: activities }) => {
  const [windowWidth, windowHeight] = useWindowSize({ initialWidth: 400, initialHeight: 400 });
  const [height, setHeight] = useState(400);
  const [width, setWidth] = useState(400);
  const div = useCallback(
    (node) => {
      if (node !== null) {
        setHeight(node.getBoundingClientRect().height);
        setWidth(node.getBoundingClientRect().width);
      }
    },
    [windowWidth, windowHeight],
  );

  const Viewer = useRef(null);

  useEffect(() => {
    if (Viewer?.current) {
      const handle = setTimeout(() => {
        Viewer.current.fitToViewer();
      }, 0.2);
      return () => clearTimeout(handle);
    }
  }, [Viewer]);

  const [tool, setTool] = useState(TOOL_NONE);
  const [value, setValue] = useState(INITIAL_VALUE);
  const { activityIds: selectedActivityIds, roleIds: selectedRoleIds } = useFilter();
  const dispatchFilter = useFilterDispatch();

  const { activityIds: hoveredActivityIds, roleIds: hoveredRoleIds } = useHover();
  const dispatchHover = useHoverDispatch();

  useEffect(() => {
    // Viewer.current.fitToViewer();
    const onKeyDown = (event) => {
      if (event.keyCode === 32) {
        setTool('pan');
      } else if (event.keyCode === 27) {
        setTool('none');
      }
    };
    const onKeyUp = (event) => {
      if (event.keyCode === 32) {
        setTool('none');
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  /* Read all the available methods in the documentation */
  const _zoomInViewerCenter1 = (dir) => Viewer.current.zoomOnViewerCenter(1.1);
  const _zoomOutViewerCenter1 = (dir) => Viewer.current.zoomOnViewerCenter(0.9);
  const _fitSelection1 = () => Viewer.current.fitSelection(40, 40, 200, 200);
  const _fitToViewer1 = () => Viewer.current.fitToViewer();

  /* keep attention! handling the state in the following way doesn't fire onZoom and onPam hooks */
  const _zoomOnViewerCenter2 = () => setValue(zoomOnViewerCenter(value, 1.1));
  const _fitSelection2 = () => setValue(fitSelection(value, 40, 40, 200, 200));
  const _fitToViewer2 = () => setValue(fitToViewer(value));

  const roleTagMap = {
    1: 'role-child',
    2: 'role-trafficker',
    3: 'role-buyer',
    4: 'role-law-enforcement',
    5: 'role-philanthropist',
    6: 'role-agency',
    7: 'role-legislator',
  };

  const handleRoleHovered = (roleIds, isHovered) => {
    dispatchHover({ type: isHovered ? 'role-hovered' : 'role-unhovered', roleIds });
  };

  const isRoleHovered = (roleIds) => {
    // Returns true if ANY of roleIds are hovered
    return hoveredRoleIds.some((hri) => _.castArray(roleIds).includes(hri));
  };

  const isRoleSelected = (roleIds) => {
    // Returns true if ANY of roleIds are selected
    return selectedRoleIds.some((sri) => _.castArray(roleIds).includes(sri));
  };

  const isRoleVisible = (roleIds) => {
    // Returns true if ANY of roleIds are selected or hovered
    return isRoleHovered(roleIds) || isRoleSelected(roleIds);
  };

  const handleRoleSelect = (roleId) => {
    dispatchFilter({ type: 'role-set', roleId });
  };

  const handleActivityHovered = (activityIds, isHovered) => {
    dispatchHover({ type: isHovered ? 'activity-hovered' : 'activity-unhovered', activityIds });
    const as = activities.filter((ea) => _.castArray(activityIds).includes(ea.ID));
    const allRoleIds = as.reduce((p, c) => [...p, ...c.ROLE_IDS], []);
    handleRoleHovered(allRoleIds, isHovered);
    dispatchHover({ type: isHovered ? 'role-hovered' : 'role-unhovered', roleIds: allRoleIds });
  };

  const isActivityHovered = (activityId) => {
    return hoveredActivityIds.length > 0 && _.difference(hoveredActivityIds, [activityId]).length === 0;
  };

  const handleActivitySelected = (activityId) => {
    dispatchFilter({ type: 'activity-set', activityId });
    const as = activities.filter((ea) => _.castArray(activityId).includes(ea.ID));
    const allRoleIds = as.reduce((p, c) => [...p, ...c.ROLE_IDS], []);
    dispatchFilter({ type: 'role-set', roleId: allRoleIds });
  };

  const isActivitySelected = (activityId) => {
    return selectedActivityIds.includes(activityId);
  };

  const handleBackgroundSelect = (e) => {
    if (e?.originalEvent?.target?.localName === 'rect' || e?.target?.localName === 'svg') {
      dispatchFilter({ type: 'cleared' });
    }
  };

  const [isHelpVisible, setHelpVisible] = useState(window.localStorage.getItem('help-visible') !== 'false');

  const Toolbar = ({ value, onChangeValue, tool, onChangeTool }) => {
    // console.log('Toolbar(): value = %o', value);
    const selectTool = (e, toolId) => {
      onChangeTool(toolId);
      e.stopPropagation();
    };

    const closeHelp = () => {
      window.localStorage.setItem('help-visible', 'false');
      setHelpVisible(false);
    };

    const Tooltip = ({ label }) => (
      <div className="tooltip text-small">
        <div className="caret"></div>
        <div className="tooltip-label">{label}</div>
      </div>
    );

    return (
      <div className={cn('toolbar-parent-container', isHelpVisible && 'help-visible')}>
        <div className="toolbar">
          <div className={cn('tool', tool === TOOL_NONE && 'selected')} onClick={(e) => selectTool(e, TOOL_NONE)}>
            {tool === TOOL_NONE ? <SelectFill /> : <Select />}
            <Tooltip label="Click on things in the map" />
          </div>
          <div className={cn('tool', tool === TOOL_PAN && 'selected')} onClick={(e) => selectTool(e, TOOL_PAN)}>
            {tool === TOOL_PAN ? <PanFill /> : <Pan />}
            <Tooltip label="Drag to move the map around" />
          </div>
          <div className={cn('tool', tool === TOOL_ZOOM_IN && 'selected')} onClick={(e) => _zoomInViewerCenter1(1)}>
            <ZoomIn />
            <Tooltip label="Get a closer view" />
          </div>
          <div className={cn('tool', tool === TOOL_ZOOM_OUT && 'selected')} onClick={(e) => _zoomOutViewerCenter1(-1)}>
            <ZoomOut />
            <Tooltip label="See more of the map" />
          </div>
          <div
            className="tool"
            onClick={(e) => {
              _fitToViewer1();
              e.stopPropagation();
            }}
          >
            <Fit />
            <Tooltip label="Expand the map to fill your screen" />
          </div>
          <div className="spacer" />
          <div
            className="tool"
            onClick={(e) => {
              window.localStorage.setItem('help-visible', 'true');
              setHelpVisible(true);
            }}
          >
            <Question />
            <Tooltip label="Show toolbar help information" />
          </div>
        </div>
        <div className={cn('toolbar-help-container')}>
          <div className={cn('toolbar-help')}>
            <div className="toolbar-help-title text-large text-bold">How to Use the Map Tools</div>
            <div className="toolbar-help-subtitle">To use the map, click one of the tools below to choose what you want to do:</div>
            <div className="icon-line">
              <Select /> <span className="text-white text-bold">Clicker</span> – Click on things in the map
            </div>
            <div className="icon-line">
              <Pan /> <span className="text-white text-bold">Move</span> – Drag to move the map around
            </div>
            <div className="icon-line">
              <ZoomIn /> <span className="text-white text-bold">Zoom In</span> – Get a closer view
            </div>
            <div className="icon-line">
              <ZoomOut /> <span className="text-white text-bold">Zoom Out</span> – See more of the map
            </div>
            <div className="icon-line">
              <Fit /> <span className="text-white text-bold">Full Screen</span> – Expand the map to fill your screen
            </div>
            <div className="toolbar-help-instructions-container">
              <div className="toolbar-help-instruction">
                <span className="text-white text-bold">Step 1:</span> Choose a tool
              </div>
              <div className="toolbar-help-instruction">
                <span className="text-white text-bold">Step 2:</span> Click or move around the map to interact with it
              </div>
              <div className="toolbar-help-instruction">
                <span className="text-white text-bold">Step 3:</span> Click <Select /> to resume interacting with the map
              </div>
            </div>
            <div className="toolbar-help-button-container">
              <Button textAlign="center" color="highlight" onClick={closeHelp}>
                Close
              </Button>
            </div>
            <div className="caret"></div>
          </div>
          <div className="toolbar-help-underlay" onClick={closeHelp}></div>
        </div>
      </div>
    );
  };

  return (
    <div className="ecosystem-page" style={{ width: '100%', height: '100%' }}>
      <div className="ecosystem-container" ref={div} onClick={handleBackgroundSelect}>
        <ReactSVGPanZoom
          ref={Viewer}
          width={width}
          height={height}
          tool={tool}
          onChangeTool={setTool}
          value={value}
          onChangeValue={setValue}
          customMiniature={Mini}
          preventPanOutside={true}
          detectWheel={true}
          detectAutoPan={false}
          background="#0F172A"
          SVGBackground="#0F172A"
          scaleFactorMin={0.3}
          scaleFactorMax={1.25}
          scaleFactor={1.1}
          toolbarProps={{ position: 'left' }}
          onClick={(e) => handleBackgroundSelect(e)}
          customToolbar={Toolbar}
        >
          <svg width={1637} height={975} viewBox="-100 0 1737 975" fill="#0F172A">
            {/* <!-- Phase Labels --> */}
            <foreignObject x="137" y="541" width="150" height="35">
              <p className="phase-label">Exploitation</p>
            </foreignObject>
            <foreignObject x="205" y="170" width="160" height="35">
              <p className="phase-label">Vulnerability</p>
            </foreignObject>
            <foreignObject x="804" y="98" width="150" height="35">
              <p className="phase-label">Prevention</p>
            </foreignObject>
            <foreignObject x="868" y="468" width="90" height="35">
              <p className="phase-label">Safety</p>
            </foreignObject>
            <foreignObject x="1110" y="750" width="90" height="35">
              <p className="phase-label">Policy</p>
            </foreignObject>
            <foreignObject x="1138" y="560" width="150" height="35">
              <p className="phase-label">Prosecution</p>
            </foreignObject>
            <foreignObject x="1208" y="232" width="180" height="35">
              <p className="phase-label">Empowerment</p>
            </foreignObject>

            {/* <!-- Role Paths -->  */}
            <g
              className={cn(['role', 'role-child', isRoleVisible([1]) ? 'hovered' : ''])}
              onClick={() => handleRoleSelect(1, true)}
              onMouseOver={() => handleRoleHovered(1, true)}
              onMouseOut={() => handleRoleHovered(1, false)}
            >
              <path
                d="M698.499 577L420.499 577V577C420.224 577 420 577.223 420 577.499L420 718L668.371 718C672.614 718 676.684 716.314 679.684 713.314L811.312 581.686C814.312 578.686 818.382 577 822.625 577L1031.87 577C1036.12 577 1040.19 575.314 1043.19 572.314L1098.81 516.686C1101.81 513.686 1105.88 512 1110.13 512L1534.5 512L1534.5 444.627C1534.5 440.384 1532.81 436.314 1529.81 433.314L1440.19 343.686C1437.19 340.686 1433.12 339 1428.87 339L1156 339M1157.2 339L1047.89 229.686C1044.89 226.686 1040.82 225 1036.57 225L598.028 225C593.785 225 589.715 223.314 586.715 220.314L478.901 112.5M156 159.999L156 215.889C156 220.133 157.685 224.202 160.686 227.203L209.013 275.53C212.014 278.531 216.084 280.217 220.327 280.217L426.501 280.217C430.786 280.217 434.892 281.936 437.899 284.988L547.298 396.045C550.306 399.098 554.412 400.817 558.697 400.817L694.574 400.817C698.817 400.817 702.887 402.502 705.887 405.503L753.515 453.13C756.515 456.131 758.201 460.201 758.201 464.444L758.201 513.372C758.201 517.615 756.515 521.685 753.515 524.686L701.201 576.999M156 159.999L65.0002 69M156 159.999L199.013 116.986C202.014 113.985 206.083 112.3 210.327 112.3L478.199 112.3"
                className="path"
              />
              <path
                className="hover-path"
                d="M698.499 577L420.499 577V577C420.224 577 420 577.223 420 577.499L420 718L668.371 718C672.614 718 676.684 716.314 679.684 713.314L811.312 581.686C814.312 578.686 818.382 577 822.625 577L1031.87 577C1036.12 577 1040.19 575.314 1043.19 572.314L1098.81 516.686C1101.81 513.686 1105.88 512 1110.13 512L1534.5 512L1534.5 444.627C1534.5 440.384 1532.81 436.314 1529.81 433.314L1440.19 343.686C1437.19 340.686 1433.12 339 1428.87 339L1156 339M1157.2 339L1047.89 229.686C1044.89 226.686 1040.82 225 1036.57 225L598.028 225C593.785 225 589.715 223.314 586.715 220.314L478.901 112.5M156 159.999L156 215.889C156 220.133 157.685 224.202 160.686 227.203L209.013 275.53C212.014 278.531 216.084 280.217 220.327 280.217L426.501 280.217C430.786 280.217 434.892 281.936 437.899 284.988L547.298 396.045C550.306 399.098 554.412 400.817 558.697 400.817L694.574 400.817C698.817 400.817 702.887 402.502 705.887 405.503L753.515 453.13C756.515 456.131 758.201 460.201 758.201 464.444L758.201 513.372C758.201 517.615 756.515 521.685 753.515 524.686L701.201 576.999M156 159.999L65.0002 69M156 159.999L199.013 116.986C202.014 113.985 206.083 112.3 210.327 112.3L478.199 112.3"
              />
              <rect x="32.5" y="50.5" width="62" height="35" rx="17.5" fill="#0F172A" />
              <rect
                className="role-label-container"
                x="32.5"
                y="50.5"
                width="62"
                height="35"
                rx="17.5"
                stroke="#FB923C"
                strokeWidth={2}
                fill="#FB923C"
                fillOpacity="0.3"
              />
              <foreignObject x="32.5" y="50.5" width="62" height="35">
                <p className="role-label">Child</p>
              </foreignObject>
            </g>

            <g
              className={cn(['role', 'role-trafficker', isRoleVisible([2]) ? 'hovered' : ''])}
              onClick={() => handleRoleSelect(2, true)}
              onMouseOver={() => handleRoleHovered(2, true)}
              onMouseOut={() => handleRoleHovered(2, false)}
            >
              <path
                d="M63 328L283.666 328C288.032 328 292.209 326.216 295.227 323.06L378.574 235.939C381.592 232.784 385.769 231 390.135 231L454.873 231C459.116 231 463.186 232.686 466.186 235.686L694.343 463.843C697.467 466.967 697.467 472.033 694.343 475.157L652.186 517.314C649.186 520.314 645.116 522 640.873 522L371.127 522C366.884 522 362.782 523.718 359.781 526.719C298.679 587.821 292.821 593.679 231.719 654.781C228.718 657.782 227 661.884 227 666.127L227 751.872C227 756.116 228.686 760.186 231.686 763.186L291.186 822.686C297.435 828.935 307.565 828.935 313.814 822.686L559.5 577M1492 554.5L1492 608.873C1492 613.116 1490.31 617.186 1487.31 620.186L1407.19 700.314C1404.19 703.314 1400.12 705 1395.87 705L1346.13 705C1341.88 705 1337.81 706.686 1334.81 709.686L1201.5 843"
                className="path"
              />
              <path
                d="M63 328L283.666 328C288.032 328 292.209 326.216 295.227 323.06L378.574 235.939C381.592 232.784 385.769 231 390.135 231L454.873 231C459.116 231 463.186 232.686 466.186 235.686L694.343 463.843C697.467 466.967 697.467 472.033 694.343 475.157L652.186 517.314C649.186 520.314 645.116 522 640.873 522L371.127 522C366.884 522 362.782 523.718 359.781 526.719C298.679 587.821 292.821 593.679 231.719 654.781C228.718 657.782 227 661.884 227 666.127L227 751.872C227 756.116 228.686 760.186 231.686 763.186L291.186 822.686C297.435 828.935 307.565 828.935 313.814 822.686L559.5 577M1492 554.5L1492 608.873C1492 613.116 1490.31 617.186 1487.31 620.186L1407.19 700.314C1404.19 703.314 1400.12 705 1395.87 705L1346.13 705C1341.88 705 1337.81 706.686 1334.81 709.686L1201.5 843"
                className="hover-path"
              />
              <rect x="51.5" y="309.5" width="87" height="35" rx="17.5" fill="#0F172A" />
              <rect
                className="role-label-container"
                x="51.5"
                y="309.5"
                width="87"
                height="35"
                rx="17.5"
                stroke="#6D28D9"
                strokeWidth={2}
                fill="#6D28D9"
                fillOpacity="0.3"
              />
              <foreignObject x="51.5" y="309.5" width="87" height="35">
                <p className="role-label">Exploiter</p>
              </foreignObject>
              <rect x="1150.5" y="834.5" width="87" height="35" rx="17.5" fill="#0F172A" />
              <rect
                className="role-label-container"
                x="1150.5"
                y="834.5"
                width="87"
                height="35"
                rx="17.5"
                stroke="#6D28D9"
                strokeWidth={2}
                fill="#6D28D9"
                fillOpacity="0.3"
              />
              <foreignObject x="1150.5" y="834.5" width="87" height="35">
                <p className="role-label">Exploiter</p>
              </foreignObject>
            </g>

            <g
              className={cn(['role', 'role-buyer', isRoleVisible([3]) ? 'hovered' : ''])}
              onClick={() => handleRoleSelect(3, true)}
              onMouseOver={() => handleRoleHovered(3, true)}
              onMouseOut={() => handleRoleHovered(3, false)}
            >
              <path
                d="M52.9995 538L122.813 468.186C125.814 465.186 129.883 463.5 134.127 463.5L299.869 463.5C304.112 463.5 308.182 465.186 311.183 468.186L408.683 565.686C414.931 571.935 414.931 582.065 408.683 588.314L285.5 711.496M285.5 711.498L355.5 781.498 H 915 M 858.497 841.5L1050.31 649.686C1053.31 646.686 1057.38 645 1061.62 645L1281 645M1281 645L1326 690M1281 645L1368.81 557.186C1371.81 554.186 1375.88 552.5 1380.13 552.5L1492 552.5"
                className="path"
              />
              <path
                d="M52.9995 538L122.813 468.186C125.814 465.186 129.883 463.5 134.127 463.5L299.869 463.5C304.112 463.5 308.182 465.186 311.183 468.186L408.683 565.686C414.931 571.935 414.931 582.065 408.683 588.314L285.5 711.496M285.5 711.498L355.5 781.498 H 915 M 858.497 841.5L1050.31 649.686C1053.31 646.686 1057.38 645 1061.62 645L1281 645M1281 645L1326 690M1281 645L1368.81 557.186C1371.81 554.186 1375.88 552.5 1380.13 552.5L1492 552.5"
                className="hover-path"
              />
              <rect x="33.5" y="504.5" width="67" height="35" rx="17.5" fill="#0F172A" />
              <rect
                className="role-label-container"
                x="22.5"
                y="504.5"
                width="90"
                height="35"
                rx="17.5"
                stroke="#A21CAF"
                strokeWidth={2}
                fill="#A21CAF"
                fillOpacity="0.3"
              />
              <foreignObject x="33.5" y="504.5" width="67" height="35">
                <p className="role-label">Consumer</p>
              </foreignObject>
              <rect x="824.5" y="834.5" width="67" height="35" rx="17.5" fill="#0F172A" />
              <rect
                className="role-label-container"
                x="813.5"
                y="834.5"
                width="90"
                height="35"
                rx="17.5"
                stroke="#A21CAF"
                strokeWidth={2}
                fill="#A21CAF"
                fillOpacity="0.3"
              />
              <foreignObject x="824.5" y="834.5" width="67" height="35">
                <p className="role-label">Consumer</p>
              </foreignObject>
            </g>

            <g
              className={cn(['role', 'role-law-enforcement', isRoleVisible([4]) ? 'hovered' : ''])}
              onClick={() => handleRoleSelect(4, true)}
              onMouseOver={() => handleRoleHovered(4, true)}
              onMouseOut={() => handleRoleHovered(4, false)}
            >
              <path
                d="M559.999 68.5L845.314 353.815C848.315 356.816 852.384 358.501 856.628 358.501L896.876 358.501C901.119 358.501 905.189 360.187 908.19 363.188L993.816 448.814C996.817 451.814 998.502 455.884 998.502 460.127L998.502 508.622C998.502 512.865 996.817 516.935 993.816 519.935L940.938 572.814C937.937 575.814 936.251 579.884 936.251 584.127L936.251 635.873C936.251 640.116 937.937 644.186 940.938 647.186L993.816 700.065C996.817 703.065 1000.89 704.751 1005.13 704.751L1334.37 704.751C1338.62 704.751 1342.69 703.065 1345.69 700.065L1534.5 511.251"
                className="path"
              />
              <path
                d="M559.999 68.5L845.314 353.815C848.315 356.816 852.384 358.501 856.628 358.501L896.876 358.501C901.119 358.501 905.189 360.187 908.19 363.188L993.816 448.814C996.817 451.814 998.502 455.884 998.502 460.127L998.502 508.622C998.502 512.865 996.817 516.935 993.816 519.935L940.938 572.814C937.937 575.814 936.251 579.884 936.251 584.127L936.251 635.873C936.251 640.116 937.937 644.186 940.938 647.186L993.816 700.065C996.817 703.065 1000.89 704.751 1005.13 704.751L1334.37 704.751C1338.62 704.751 1342.69 703.065 1345.69 700.065L1534.5 511.251"
                className="hover-path"
                onClick={() => handleRoleSelect(4, true)}
                onMouseOver={() => handleRoleHovered(4, true)}
                onMouseOut={() => handleRoleHovered(4, false)}
              />
              <rect x="464.5" y="50.5" width="135" height="35" rx="17.5" fill="#0F172A" />
              <rect
                className="role-label-container"
                x="464.5"
                y="50.5"
                width="135"
                height="35"
                rx="17.5"
                stroke="#0E7490"
                strokeWidth={2}
                fill="#0E7490"
                fillOpacity="0.3"
              />
              <foreignObject x="464.5" y="50.5" width="135" height="35">
                <p className="role-label">Law Enforcement</p>
              </foreignObject>
            </g>

            <g
              className={cn(['role', 'role-philanthropist', isRoleVisible([5]) ? 'hovered' : ''])}
              onClick={() => handleRoleSelect(5, true)}
              onMouseOver={() => handleRoleHovered(5, true)}
              onMouseOut={() => handleRoleHovered(5, false)}
            >
              <path
                d="M672.497 68.5L766.313 162.318C769.314 165.318 773.383 167.004 777.627 167.004L839.37 167.004C843.614 167.004 847.683 168.69 850.684 171.69L899.308 220.314C902.308 223.314 903.994 227.384 903.994 231.627L903.994 352C903.994 355.314 906.68 358 909.994 358L1033.37 358C1037.61 358 1041.68 359.686 1044.68 362.686L1102.31 420.314C1105.31 423.314 1109.38 425 1113.63 425L1242 425"
                className="path"
              />
              <path
                d="M672.497 68.5L766.313 162.318C769.314 165.318 773.383 167.004 777.627 167.004L839.37 167.004C843.614 167.004 847.683 168.69 850.684 171.69L899.308 220.314C902.308 223.314 903.994 227.384 903.994 231.627L903.994 352C903.994 355.314 906.68 358 909.994 358L1033.37 358C1037.61 358 1041.68 359.686 1044.68 362.686L1102.31 420.314C1105.31 423.314 1109.38 425 1113.63 425L1242 425"
                className="hover-path"
              />
              <path d="M1560.5 852L1499.69 791.187C1496.69 788.186 1492.62 786.5 1488.37 786.5L1421 786.5" className="path" />
              <path d="M1560.5 852L1499.69 791.187C1496.69 788.186 1492.62 786.5 1488.37 786.5L1421 786.5" className="hover-path" />
              <rect x="620.5" y="50.5" width="105" height="35" rx="17.5" fill="#0F172A" />
              <rect
                className="role-label-container"
                x="620.5"
                y="50.5"
                width="105"
                height="35"
                rx="17.5"
                stroke="#15803D"
                strokeWidth={2}
                fill="#15803D"
                fillOpacity="0.3"
              />
              <foreignObject x="620.5" y="50.5" width="105" height="35">
                <p className="role-label">Philanthropist</p>
              </foreignObject>
              <rect x="1499.5" y="834.5" width="105" height="35" rx="17.5" fill="#0F172A" />
              <rect
                className="role-label-container"
                x="1499.5"
                y="834.5"
                width="105"
                height="35"
                rx="17.5"
                stroke="#15803D"
                strokeWidth={2}
                fill="#15803D"
                fillOpacity="0.3"
              />
              <foreignObject x="1499.5" y="834.5" width="105" height="35">
                <p className="role-label">Philanthropist</p>
              </foreignObject>
            </g>

            <g
              className={cn(['role', 'role-agency', isRoleVisible([6]) ? 'hovered' : ''])}
              onClick={() => handleRoleSelect(6, true)}
              onMouseOver={() => handleRoleHovered(6, true)}
              onMouseOut={() => handleRoleHovered(6, false)}
            >
              <path
                d="M920.499 781L818.687 679.188C815.686 676.188 814.001 672.118 814.001 667.875L814.001 576.502L814.001 448.129C814.001 443.886 812.315 439.816 809.314 436.815L661.328 288.83C658.808 286.31 660.592 282.001 664.156 282.001L840.374 282.001C844.617 282.001 848.687 280.316 851.687 277.315L904.001 225.001L1049.69 79.3144C1055.93 73.0659 1066.06 73.0659 1072.31 79.3142L1150.81 157.814C1153.81 160.814 1155.5 164.884 1155.5 169.127L1155.5 331.873C1155.5 336.116 1157.19 340.186 1160.19 343.186L1242.5 425.5"
                className="path"
              />
              <path
                d="M920.499 781L818.687 679.188C815.686 676.188 814.001 672.118 814.001 667.875L814.001 576.502L814.001 448.129C814.001 443.886 812.315 439.816 809.314 436.815L661.328 288.83C658.808 286.31 660.592 282.001 664.156 282.001L840.374 282.001C844.617 282.001 848.687 280.316 851.687 277.315L904.001 225.001L1049.69 79.3144C1055.93 73.0659 1066.06 73.0659 1072.31 79.3142L1150.81 157.814C1153.81 160.814 1155.5 164.884 1155.5 169.127L1155.5 331.873C1155.5 336.116 1157.19 340.186 1160.19 343.186L1242.5 425.5"
                className="hover-path"
              />
              <rect x="1022.5" y="50.5" width="77" height="35" rx="17.5" fill="#0F172A" />
              <rect
                className="role-label-container"
                x="1022.5"
                y="50.5"
                width="77"
                height="35"
                rx="17.5"
                stroke="#1D4ED8"
                strokeWidth={2}
                fill="#1D4ED8"
                fillOpacity="0.3"
              />
              <foreignObject x="1022.5" y="50.5" width="77" height="35">
                <p className="role-label">Agency</p>
              </foreignObject>
            </g>

            <g
              className={cn(['role', 'role-legislator', isRoleVisible([7]) ? 'hovered' : ''])}
              onClick={() => handleRoleSelect(7, true)}
              onMouseOver={() => handleRoleHovered(7, true)}
              onMouseOut={() => handleRoleHovered(7, false)}
            >
              <path d="M1341.5 706.002L1417.31 781.814C1420.31 784.815 1422 788.884 1422 793.128L1422 852" className="path" />
              <path d="M1341.5 706.002L1417.31 781.814C1420.31 784.815 1422 788.884 1422 793.128L1422 852" className="hover-path" />
              <rect x="1376.5" y="834.5" width="90" height="35" rx="17.5" fill="#0F172A" />
              <rect x="1376.5" y="834.5" width="90" height="35" rx="17.5" stroke="#2563EB" strokeWidth={2} fill="#2563EB" fillOpacity="0.3" />
              <foreignObject x="1376.5" y="834.5" width="90" height="35">
                <p className="role-label">Legislator</p>
              </foreignObject>
            </g>

            {/* <!-- Pill shaped activities -->  */}
            {activityNodes
              .filter((an) => an.type === 'pill')
              .map((an, index) => (
                <g
                  key={an.ID + '-' + index}
                  className={cn([isRoleVisible(an.roles) ? 'visible' : ''])}
                  onMouseOver={() => handleActivityHovered(an.ID, true)}
                  onMouseOut={() => handleActivityHovered(an.ID, false)}
                  onClick={() => handleActivitySelected(an.ID)}
                >
                  <path
                    d={an.d}
                    transform-origin={an.to}
                    className={cn([
                      'activity',
                      'pill',
                      ...an.roles.map((r) => roleTagMap[r]),
                      isActivityHovered(an.ID) ? 'hovered' : '',
                      isActivitySelected(an.ID) ? 'selected' : '',
                    ])}
                  />
                  <text x={an.labelX} y={an.labelY} className="activity-label">
                    {an.label}
                  </text>
                </g>
              ))}
            {/* 

          {/* <!-- Image shaped activities -->  */}
            {activityNodes
              .filter((an) => an.type === 'image')
              .map((an, index) => (
                <g
                  key={an.ID + '-' + index}
                  className={cn([isRoleVisible(an.roles) ? 'visible' : ''])}
                  onMouseOver={() => handleActivityHovered(an.ID, true)}
                  onMouseOut={() => handleActivityHovered(an.ID, false)}
                  onClick={() => handleActivitySelected(an.ID)}
                >
                  <circle
                    cx={an.cx}
                    cy={an.cy}
                    r="24"
                    transform-origin={an.cx + ' ' + an.cy}
                    className={cn([
                      'activity',
                      'image',
                      ...an.roles.map((r) => roleTagMap[r]),
                      isActivityHovered(an.ID) ? 'hovered' : '',
                      isActivitySelected(an.ID) ? 'selected' : '',
                    ])}
                  />
                  <image href={an.href} x={an.cx - 12} y={an.cy - 12} width="24" height="24"></image>
                  <foreignObject x={an.labelX} y={an.labelY} width={an.labelWidth} height={an.labelHeight}>
                    <p className="activity-label">{an.label}</p>
                  </foreignObject>
                </g>
              ))}

            {/* <!-- Circle shaped activities -->  */}
            {activityNodes
              .filter((an) => an.type === 'dot')
              .map((an, index) => (
                <g
                  key={an.ID + '-' + index}
                  className={cn([isRoleVisible(an.roles) ? 'visible' : ''])}
                  onMouseOver={() => handleActivityHovered(an.ID, true)}
                  onMouseOut={() => handleActivityHovered(an.ID, false)}
                  onClick={() => handleActivitySelected(an.ID)}
                >
                  <circle
                    cx={an.cx}
                    cy={an.cy}
                    r="4"
                    transform-origin={an.cx + ' ' + an.cy}
                    className={cn([
                      'activity',
                      'dot',
                      // ...an.roles.map((r) => roleTagMap[r]),
                      roleTagMap[an.roles[0]],
                      isActivityHovered(an.ID) ? 'hovered' : '',
                      isActivitySelected(an.ID) ? 'selected' : '',
                    ])}
                  />
                  <foreignObject x={an.labelX} y={an.labelY} width={an.labelWidth} height={an.labelHeight}>
                    <p className="activity-label" style={{ textAlign: an.align, backgroundColor: an.labelBackground || 'none' }}>
                      {an.label}
                    </p>
                  </foreignObject>
                </g>
              ))}

            <defs></defs>
          </svg>
        </ReactSVGPanZoom>
      </div>
    </div>
  );
};
